import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AttributeCarouselProducts, { ProductCarouselProductStyles } from "@MasterBluePrint/Components/AttributeCarouselProducts";
import LazyLoadWrapper from "@MasterBluePrint/Components/LazyLoadWrapper";
import logProductImpression from "@MasterBluePrint/Handlers/ProductImpressionTrackingHandler";
import themeColors from "@MasterBluePrint/Styles/variables";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showBreakPrice = "showBreakPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    hideProductPriceSavingsMessage = "hideProductPriceSavingsMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showBreakPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.hideProductPriceSavingsMessage]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        state
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    logProductImpression,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductAttributeCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    slidesNumberGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    sliderHeader?: InjectableCss;
    sliderArrows?: InjectableCss;
}

export const ProductAttributeCarouselStyles: ProductAttributeCarouselStyles = {
    sliderHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            border-width: 1.25rem;
            border-bottom: 1px solid #C2D1D9;
            padding-bottom: 3px;
            margin-bottom: 1rem;
            margin-right: .625rem;
            @media (max-width: 991px) {
                padding-left: 0;
                padding-bottom: 0;
            }

            @media (max-width: 768px) {
                margin-left: 1rem;
            }            
        `,
    },
    sliderArrows: {
        css: css`
            display: flex;
            gap: 0;
            justify-content: end;
            @media (max-width: 991px) {
                justify-content: left;
            }
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 1.25rem;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            margin-bottom: 1rem;
            text-align: center;
            margin-bottom: 0;
            line-height: 2rem;
            color: #002554;
            font-size: 20px;
            font-weight: 500;
            letter-spacing: 0;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 1.25rem;
            background: ${themeColors.colorWhite};
            padding: 2.5rem 3.75rem 2.5rem 2.5rem;
            position: relative;
            width: auto;
            @media screen and (max-width: 1199px) and (min-width: 992px) {
                width: 94%;
                padding: 2.5rem .9375rem 2.5rem .9375rem;
            }
            @media (max-width: 991px) {
                margin: 0;
                padding: .9375rem 0;
                max-width: 100%;
                width: 100%;
                > div {
                    width: 100%;
                }
            }
            span.stock-container {
                min-height: 43px;
                margin-top: 10px;
            }
            div.price-wrapper {
                display: none;
            }

            /*New design changes*/
            @media screen and (min-width: 768px) {
                padding: 2rem 2.1rem 3.45rem 1rem;
                max-width: 100%;
                margin: 0.625rem 0 1.875rem;
                border: 1px solid rgba(0, 37, 84, 0.08);
            }

            .carousel-header {
                margin-bottom: 1rem;
                padding-bottom: 3px;

                h2 {
                    color: #002554;
                    font-size: 20px;
                    font-weight: 500;
                    letter-spacing: 0;
                }
            }
            [data-test-selector="productImage"] {
                margin-bottom: 20px;
            }
            .show-price-link {
                display: none;
            }
            .mfg-container:not(.new-attr) {
                span {
                    display: inline-block;
                    &:first-child {
                        margin-right: 5px;
                        &:after {
                            content: ":";
                            display: inline-block;
                        }
                    }
                }
            }
            .part-number-container {
                span {
                    margin-bottom: 0;
                }
            }
            .part-number-container,
            .mfg-container,
            .new-attr {
                width: 100%;
                max-width: 100%;
                span {
                    display: inline-block;
                    &:first-child {
                        margin-right: 5px;
                        color: #333F48;
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 24px;
                        font-family: 'Lato', sans-serif;
                    }
                    &:last-child {
                        color: #535E64;
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 24px;
                        font-family: 'Lato', sans-serif;
                    }
                }
                @media screen and (min-width: 1400px) {
                    max-width: 100%;
                }
            }
            div[adjust-spacing] {
                max-width: 100%;
            }
            div[class^="InventoryMessage"],
            div[class*=" InventoryMessage"] {
                width: 100%;
            }
            div[class^="InventoryMessage"][color="success"],
            div[class*=" InventoryMessage"][color="success"] {
                width: 100%;
            }
            div[class^="InventoryMessage"][color="danger"],
            div[class*=" InventoryMessage"][color="danger"] {
                width: 100%;
                span.stock-container {
                    span {
                        line-height: 20px;
                    }

                    &:nth-child(even) {
                        background: #F2F5F7;
                    }
                    &:nth-child(odd) {
                        background: #fff;
                    }
                }
            }
            span.stock-container {
                min-height: 10px;
                > div {
                    padding: 11px 0;
                    &:nth-child(even) {
                        background: #F2F5F7;
                    }
                    &:nth-child(odd) {
                        background: #fff;
                    }
                    
                    > span {
                        > span {
                            font-size: 14px !important;
                            line-height: 20px;
                            letter-spacing: 0;
                            font-family: 'Lato', sans-serif !important;
                            &:fist-child {
                                color: #535E64 !important;
                            }
                        }
                    }
                }
                
            }
            .product-detail-link {
                padding-bottom: 12px;
                margin-bottom: 12px;
                border-bottom: 1px solid #C2D1D9;
                span {
                    font-size: 16px;
                }
            }
            .price-section {
                margin-top: 1.4rem;
                //margin-bottom: 2.0625rem;
                margin-bottom: 1.5rem;
                span[data-test-selector="productPrice_unitNetPrice"] {
                    font-size: 22px !important;
                    font-weight: bold;
                    letter-spacing: -0.42px;
                }
                span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                    font-family: Roboto;
                    font-size: 16px !important;
                    letter-spacing: 1.14px;
                }
            }
            div[data-test-selector="carousal-addtocart-wrapper"] {
                button {
                    span {
                        font-size: 16px;
                    }
                }
            }
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            flex-basis: 100%;
            max-width: 100%;
            &:hover {
                border:none;
                background:none;
            }
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            .KWUcc svg path {
                fill: #2060AD;
            }
            &:hover {
                border:none !important;
                background: none !important;
            }
        `,
    },
    slidesNumberGridItem: {
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 100%;
            max-width: 100%;
            min-width: 100px;
            justify-content: center;
            font-family: 'Lato', sans-serif;
            color: #333F48;
            font-size: 1rem;

            span {
                font-family: 'Lato',sans-serif;
                color: #333F48;
                font-size: 1rem;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            .KWUcc svg path {
                fill: #2060AD;
            }
            &:hover {
                border:none !important;
                background: none !important;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 95%;
                        max-width: 95%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            min-height: initial !important;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: .75rem;
            display: flex;
            flex-direction: column;

            div[class^="InventoryMessage"],
            div[class*=" InventoryMessage"] {
                margin-top: 0 !important;

                &.ship-manufacture-align {
                    margin-top: 0 !important;

                    span:nth-child(even) {
                        padding: 11px 0;
                    }
                    span:nth-child(odd) {
                        background: #fff;
                        padding: 11px 0;
                    }
                }
            }
            
            .btn-present {
                padding-bottom: 10px;
                height: 100%;

                .adjust-spacing {
                    max-width: 100%;
                }
            }

            .vat-field {
                margin-top: .5rem;
            }

            .btn-not-present {
                padding-bottom: 20px !important;
            }

            @media screen and (max-width: 1199px) and (min-width: 992px) {
                padding: 0 .75rem;
            }
        `,
    },
};

const styles = ProductAttributeCarouselStyles;

const ProductAttributeCarousel: FC<Props> = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    logProductImpression,
    state
}) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [totalSlides, setTotalSlides] = useState(products?.length);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
        if (embla) {
            const selectedScrollSnap = embla.selectedScrollSnap();
            setCurrentSlideIndex(selectedScrollSnap);
            setTotalSlides(embla.scrollSnapList().length);
        }
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = useState(getDraggable());

    const hasRunOnce = useRef(false);
    useEffect(() => {
        if (hasRunOnce.current) return;

        if (products && products.length > 0 && !state.pages.productDetails.isProductLoading) {            
            hasRunOnce.current = true;
            logProductImpression({ productCollection: products, listType: translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel"), carouselTitle: fields.title });
        }
    }, [products]);

    useEffect(() => {
        loadCarouselProducts({
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        productContext?.product?.id,
        category?.id,
        brand?.id,
        isCarouselVisible
    ]);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        if (typeof window !== 'undefined') {
            window.addEventListener("resize", onWindowResize);
            onWindowResize();
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener("resize", onWindowResize);
            }
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
             watchDrag: draggable,
            loop: products.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, products]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const title =
        fields.title ||
        translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Attribute Carousel");
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);

    let slides: NodeListOf<HTMLElement> | null = null;
    let prevButton: HTMLElement | null = null;
    let nextButton: HTMLElement | null = null;
    let sliderDraggable: HTMLElement | null = null;

    if (typeof document !== 'undefined') {
        slides = document.querySelectorAll<HTMLElement>('[data-test-selector="slides"]');
        prevButton = document.querySelector<HTMLElement>('[data-test-selector="prevBtn"]');
        nextButton = document.querySelector<HTMLElement>('[data-test-selector="nextBtn"]');
        sliderDraggable = document.querySelector<HTMLElement>('.is-draggable');
    }
    
    function setMinHeightAndSpacing() {
        setTimeout(() => {
            if (slides) {
                slides.forEach(slide => {
                    const priceSections = slide.querySelectorAll<HTMLElement>('.is-selected .price-section');
                    const adjustSpacings = slide.querySelectorAll<HTMLElement>('.is-selected .adjust-spacing');
                    const productDetailLinks = slide.querySelectorAll<HTMLElement>('.is-selected .product-detail-link');
                    const mfgContainers = slide.querySelectorAll<HTMLElement>('.is-selected .mfg-container');
                    const partNoContainers = slide.querySelectorAll<HTMLElement>('.is-selected .part-number-container');
                    const attrWrappers = slide.querySelectorAll<HTMLElement>('.is-selected .attribute-wrapper');
                    let maxHeightPriceContainer = 0;
                    let maxHeightMsgContainer = 0;
                    let maxHeightPDLContainer = 0;
                    let maxHeightMFGContainer = 0;
                    let maxHeightAttrWrapper = 0;
                    let maxHeightPartNoContainer = 0;

                    // Clear previously set min heights
                    priceSections.forEach(priceSection => {
                        (priceSection as HTMLElement).style.minHeight = '';
                    });

                    adjustSpacings.forEach(adjustSpacing => {
                        (adjustSpacing as HTMLElement).style.minHeight = '';
                    });

                    productDetailLinks.forEach(productDetailLink => {
                        (productDetailLink as HTMLElement).style.minHeight = '';
                    });

                    mfgContainers.forEach(mfgContainer => {
                        (mfgContainer as HTMLElement).style.minHeight = '';
                    });

                    attrWrappers.forEach(attrWrapper => {
                        (attrWrapper as HTMLElement).style.minHeight = '';
                    });

                    partNoContainers.forEach(partNoContainer => {
                        (partNoContainer as HTMLElement).style.minHeight = '';
                    });

                    // Find the maximum height among priceSections and adjustSpacings
                    priceSections.forEach(priceSection => {
                        const currentHeightPS = (priceSection as HTMLElement).offsetHeight;

                        if (currentHeightPS > maxHeightPriceContainer) {
                            maxHeightPriceContainer = currentHeightPS;
                        }
                    });

                    adjustSpacings.forEach(adjustSpacing => {
                        const currentHeightMC = (adjustSpacing as HTMLElement).offsetHeight;

                        if (currentHeightMC > maxHeightMsgContainer) {
                            maxHeightMsgContainer = currentHeightMC;
                        }
                    });

                    productDetailLinks.forEach(productDetailLink => {
                        const currentHeightPD = (productDetailLink as HTMLElement).offsetHeight;

                        if (currentHeightPD > maxHeightPDLContainer) {
                            maxHeightPDLContainer = currentHeightPD;
                        }
                    });

                    mfgContainers.forEach(mfgContainer => {
                        const currentHeightMFG = (mfgContainer as HTMLElement).offsetHeight;

                        if (currentHeightMFG > maxHeightMFGContainer) {
                            maxHeightMFGContainer = currentHeightMFG;
                        }
                    });

                    attrWrappers.forEach(attrWrapper => {
                        const currentHeightAttrWrapper = (attrWrapper as HTMLElement).offsetHeight;

                        if (currentHeightAttrWrapper > maxHeightAttrWrapper) {
                            maxHeightAttrWrapper = currentHeightAttrWrapper;
                        }
                    });

                    partNoContainers.forEach(partNoContainer => {
                        const currentHeightPartNoContainer = (partNoContainer as HTMLElement).offsetHeight;

                        if (currentHeightPartNoContainer > maxHeightPartNoContainer) {
                            maxHeightPartNoContainer = currentHeightPartNoContainer;
                        }
                    });

                    // Set the minimum height for adjustSpacings and priceSections
                    priceSections.forEach(priceSection => {
                        (priceSection as HTMLElement).style.minHeight = `${maxHeightPriceContainer}px`;
                    });

                    adjustSpacings.forEach(adjustSpacing => {
                        (adjustSpacing as HTMLElement).style.minHeight = `${maxHeightMsgContainer}px`;
                    });

                    productDetailLinks.forEach(productDetailLink => {
                        (productDetailLink as HTMLElement).style.minHeight = `${maxHeightPDLContainer}px`;
                    });

                    mfgContainers.forEach(mfgContainer => {
                        (mfgContainer as HTMLElement).style.minHeight = `${maxHeightMFGContainer}px`;
                    });

                    attrWrappers.forEach(attrWrapper => {
                        (attrWrapper as HTMLElement).style.minHeight = `${maxHeightAttrWrapper}px`;
                    });

                    partNoContainers.forEach(partNoContainer => {
                        (partNoContainer as HTMLElement).style.minHeight = `${maxHeightPartNoContainer}px`;
                    });
                });
            }
        }, 700);
    }

    function handlePrevButtonClick() {
        if (embla) {
            setMinHeightAndSpacing();
            setCurrentSlideIndex(embla.selectedScrollSnap());
        }
    }

    function handleNextButtonClick() {
        if (embla) {
            setMinHeightAndSpacing();
            setCurrentSlideIndex(embla.selectedScrollSnap());
        }
    }

    function handleDrag() {
        setMinHeightAndSpacing();
    }

    setMinHeightAndSpacing();

    if (typeof window !== 'undefined') {
        window.addEventListener('resize', setMinHeightAndSpacing);
    }

    if (sliderDraggable && typeof window !== 'undefined') {
        if (window.innerWidth < 768) {
            sliderDraggable.addEventListener('touchend', handleDrag);
        }
    }

    if (prevButton) {
        prevButton.addEventListener('click', handlePrevButtonClick);
    }

    if (nextButton) {
        nextButton.addEventListener('click', handleNextButtonClick);
    }

    let seriesName = "";
    if (productContext?.product.attributeTypes && typeof window !== 'undefined' && window.innerWidth > 768) {
        const seriesObject = productContext?.product.attributeTypes.find(item => item.name?.toLowerCase() === "series");
        const attributeValues = seriesObject ? seriesObject.attributeValues : [];

        if (attributeValues) {
            seriesName = attributeValues.length > 0 ? ` : ${attributeValues[0]?.value}` : "";
        }
    }

    return (
        <LazyLoadWrapper setIsVisible={setIsCarouselVisible}>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <GridContainer
                {...styles.mainContainer}
                className="new-carousal-design"
                data-test-selector={`ProductAttributeCarousel_${fields.carouselType}${fields.relatedProductType}`}
            >
                <StyledWrapper {...styles.sliderHeader}>
                    <Typography {...styles.titleText}>{title} {seriesName}</Typography>

                    <StyledWrapper {...styles.sliderArrows}>
                        {products.length > slidesToScroll && (
                            <GridItem {...styles.prevArrowGridItem}>
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    data-test-selector="prevBtn"
                                    aria-label="previous button"
                                >
                                    <span>
                                        <svg width="19" height="14" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M7.41 1.41 2.47 6.35H18.2v1.3H2.47l4.94 4.94-.91.91L0 7 6.5.5z"
                                                fill="#2060AD"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                </Button>
                            </GridItem>
                        )}

                        <GridItem {...styles.slidesNumberGridItem}>
                            <Typography>{`${currentSlideIndex + 1} of ${totalSlides}`}</Typography>
                        </GridItem>
                        <GridItem {...styles.nextArrowGridItem}>
                            {products.length > slidesToScroll && (
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    data-test-selector="nextBtn"
                                    aria-label="next button"
                                >
                                    <span>
                                        <svg width="19" height="14" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="m10.79 1.41 4.94 4.94H0v1.3h15.73l-4.94 4.94.91.91L18.2 7 11.7.5z"
                                                fill="#2060AD"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                </Button>
                            )}
                        </GridItem>
                    </StyledWrapper>
                </StyledWrapper>

                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {products.map(product => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={product.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            id={`product_${product.id}`}
                                            data-test-selector="productContainer"
                                        >
                                            <AttributeCarouselProducts
                                                carouselId={id}
                                                product={product}
                                                showImage={fields.showImage}
                                                showBrand={showBrandBlocks}
                                                showTitle={fields.showTitle}
                                                showPartNumbers={fields.showPartNumbers}
                                                showPrice={fields.showPrice}
                                                showBreakPrice={fields.showBreakPrice}
                                                showAddToCart={fields.showAddToCart}
                                                showAddToList={fields.showAddToList}
                                                extendedStyles={styles.carouselProductStyles}
                                                carouselType={
                                                    carouselTypeOptions.find(o => o.value === fields.carouselType)
                                                        ?.displayName || "Product Attribute Carousel"
                                                }
                                                index={products.findIndex(e => e.id === product.id)}
                                                relatedProductType={fields.relatedProductType}
                                                hideProductPriceSavingsMessage={fields.hideProductPriceSavingsMessage}
                                                carouselTitle={title}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </LazyLoadWrapper>
    );
};

const carouselTypeOptions = [
    { value: "relatedProducts", displayName: "Product Attribute" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductAttributeCarousel))),
    definition: {
        group: "Common",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "relatedProducts",
                fieldType: "General",
                sortOrder: 1,
                customFilter: (item, page, settings) => {
                    return (
                        settings.settingsCollection.websiteSettings.enableDynamicRecommendations ||
                        (item.value !== "topSellers" && item.value !== "CrossSell" && item.value !== "Accessories")
                    );
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 2,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 3,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 4,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "General",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 6,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 5,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showBreakPrice,
                displayName: "Break Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 15,
            },
            {
                name: fields.hideProductPriceSavingsMessage,
                displayName: "Hide List Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 16,
            },
        ],
    },
};

export default widgetModule;